import { signOut } from 'aws-amplify/auth'
import variables from 'variables/variables'

export const logoutReasons = ['sessionExpired', 'guestSessionExpired'] as const

export type LogoutReason = typeof logoutReasons[number]

export const loggedOutReasonParam = 'loggedOutReason'

/**
 * Copy is keyed by a whitelisted reason so the login page never renders text taken
 * from the URL (a crafted link could otherwise put arbitrary text above the form).
 */
export const logoutReasonMessages: Record<LogoutReason, string> = {
   sessionExpired: 'Your session expired, so we logged you out to keep your data safe. Please sign in again to continue.',
   guestSessionExpired: 'We logged you out because your session expired. Open the link you were sent to get back to your document.',
}

export function isLogoutReason(value: string): value is LogoutReason {
   return (logoutReasons as readonly string[]).includes(value)
}

/** Cognito errors after which Amplify deletes the stored tokens, so the session cannot recover. */
const SESSION_ENDING_REFRESH_ERRORS = [
   'NotAuthorizedException',
   'TokenRevokedException',
   'UserNotFoundException',
   'PasswordResetRequiredException',
   'UserNotConfirmedException',
   'RefreshTokenReuseException',
]

/**
 * Whether a failed token refresh means Cognito rejected the session, rather than the refresh failing in passing.
 * @param error The error Amplify reported with `tokenRefresh_failure`.
 * @returns True when the user has to sign in again.
 */
export function isSessionEndingRefreshError(error: unknown): boolean {
   const errorName = (error as { name?: unknown } | null | undefined)?.name
   return typeof errorName === 'string' && SESSION_ENDING_REFRESH_ERRORS.some(sessionEndingName => errorName.startsWith(sessionEndingName))
}

let isRedirectingToLogin = false
let hasUserEndedSession = false

export function isForceLogoutInProgress() {
   return isRedirectingToLogin
}

/** Records that the user ended the session deliberately. */
export function markSessionEndedByUser() {
   hasUserEndedSession = true
}

/** Records that a new session has begun, re-arming the forced logout. */
export function markSessionStarted() {
   hasUserEndedSession = false
}

/**
 * Clears the session and sends the user to the login page, where the reason renders as a
 * message box above the form. For sessions that are dead and cannot recover (for example a
 * guest token rejected by the API Gateway JWT authorizer). The full-page navigation also
 * stops every polling loop in the tab, so dead sessions stop hammering the API.
 */
export async function forceLogoutWithReason(params: { reason: LogoutReason }) {
   // A request issued before a deliberate logout can answer 401 long after it, and the
   // redirect below would take over whatever page the user has since opened.
   if (isRedirectingToLogin || hasUserEndedSession) {
      return
   }
   isRedirectingToLogin = true

   localStorage.removeItem(variables.gapIdTokenLocalStorageKey)
   localStorage.removeItem(variables.gapAccessTokenLocalStorageKey)
   localStorage.removeItem(variables.gapRefreshTokenLocalStorageKey)

   try {
      await signOut()
   } catch {
      // Best effort: guests are not signed in to amplify at all
   }

   window.location.href = `${variables.regionBaseUrl}?${loggedOutReasonParam}=${params.reason}`
}
